/**
 * quickAddParser — deterministic token grammar for the single-line quick-add.
 *
 * Extracts structured task fields from one line of text and returns a cleaned
 * title. Pure and instant (no AI); see reqts/quick-add-token-grammar.md.
 *
 *   #project  @label  p1..p4 (or !1..!4)  ~<estimate>  *<recurrence>
 *   natural date phrase (chrono)          // description tail
 */

import * as chrono from 'chrono-node';

export interface QuickAddContext {
  projects: { id: string; name: string }[];
  labels: string[];
}

export interface ParsedQuickAdd {
  content: string;
  description?: string;
  projectId?: string;
  newProjectName?: string;
  labels: string[];
  priority?: number;
  timeEstimate?: string;
  recurrenceRule?: string;
  dueDate?: string;
  dueTime?: string;
}

// ~token → the existing time-estimate label bucket.
const ESTIMATE_BUCKETS: Record<string, string> = {
  '15m': '15-minutes',
  '30m': '30-minutes',
  '45m': '45-minutes',
  '1h': '1-hour',
  '90m': '90-minutes',
  '2h': '2-hours',
  breakdown: 'needs-breakdown',
};

// *token → recurrenceRule value (identity for the supported set).
const RECURRENCE_RULES = new Set(['daily', 'weekday', 'weekly', 'biweekly', 'monthly']);

const PROJECT_RE = /^#(.+)$/;
const LABEL_RE = /^@([A-Za-z0-9_-]+)$/;
const PRIORITY_RE = /^(?:p|!)([1-4])$/i;
const ESTIMATE_RE = /^~(.+)$/;
const RECURRENCE_RE = /^\*(.+)$/;

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

// ── Autocomplete ────────────────────────────────────────────────────────────

export interface QuickAddSuggestion {
  kind: 'project' | 'create-project' | 'label' | 'priority' | 'estimate' | 'recurrence';
  /** Human-readable row text. */
  label: string;
  /** Full token to substitute for the active chunk (a trailing space is added on apply). */
  token: string;
}

export interface ActiveSuggestions {
  /** Start index of the active chunk in the text. */
  start: number;
  /** End index (exclusive) of the active chunk. */
  end: number;
  items: QuickAddSuggestion[];
}

const RECURRENCE_LIST = ['daily', 'weekday', 'weekly', 'biweekly', 'monthly'];

/**
 * Given the current text and caret position, return the active token's
 * suggestions, or null when the caret is not inside a recognizable token.
 */
export function getQuickAddSuggestions(
  text: string,
  caret: number,
  ctx: QuickAddContext,
): ActiveSuggestions | null {
  // Chunk = the whitespace-delimited word containing the caret.
  let start = caret;
  while (start > 0 && !/\s/.test(text[start - 1]!)) start--;
  let end = caret;
  while (end < text.length && !/\s/.test(text[end]!)) end++;

  const chunk = text.slice(start, end);
  if (!chunk) return null;

  const sigil = chunk[0]!;
  const query = chunk.slice(1);
  const q = query.toLowerCase();
  let items: QuickAddSuggestion[] = [];

  if (sigil === '#') {
    items = ctx.projects
      .filter((p) => p.name.toLowerCase().includes(q))
      .map((p) => ({ kind: 'project', label: p.name, token: `#${p.name}` }));
    const exact = ctx.projects.some((p) => p.name.toLowerCase() === q);
    if (query && !exact) {
      items.push({ kind: 'create-project', label: `Create "${query}"`, token: `#${query}` });
    }
  } else if (sigil === '@') {
    items = ctx.labels
      .filter((l) => l.toLowerCase().includes(q))
      .map((l) => ({ kind: 'label', label: l, token: `@${l}` }));
    const exact = ctx.labels.some((l) => l.toLowerCase() === q);
    if (query && !exact) {
      items.push({ kind: 'label', label: `New label "${query}"`, token: `@${query}` });
    }
  } else if (sigil === '~') {
    items = Object.entries(ESTIMATE_BUCKETS)
      .filter(([key]) => key.startsWith(q))
      .map(([key, bucket]) => ({ kind: 'estimate', label: `~${key} · ${bucket}`, token: `~${key}` }));
  } else if (sigil === '*') {
    items = RECURRENCE_LIST.filter((r) => r.startsWith(q)).map((r) => ({
      kind: 'recurrence',
      label: `*${r}`,
      token: `*${r}`,
    }));
  } else if ((sigil === 'p' || sigil === 'P' || sigil === '!') && /^[1-4]?$/.test(query)) {
    const nums = query ? [Number(query)] : [1, 2, 3, 4];
    const names = ['', 'Urgent', 'High', 'Medium', 'Low'];
    items = nums.map((n) => ({ kind: 'priority', label: `p${n} · ${names[n]}`, token: `p${n}` }));
  }

  if (items.length === 0) return null;
  return { start, end, items };
}

/** Replace the active chunk [start,end) with a token, adding a trailing space. */
export function applySuggestion(
  text: string,
  active: ActiveSuggestions,
  token: string,
): { text: string; caret: number } {
  const next = `${text.slice(0, active.start)}${token} ${text.slice(active.end)}`;
  return { text: next, caret: active.start + token.length + 1 };
}

/**
 * Parse a single quick-add line into structured fields.
 *
 * @param refDate reference date for relative phrases ("tomorrow"); defaults to now.
 */
export function parseQuickAdd(
  text: string,
  ctx: QuickAddContext,
  refDate: Date = new Date(),
): ParsedQuickAdd {
  // 1. Split off the description tail on the first `//`.
  const slashIdx = text.indexOf('//');
  const head = slashIdx === -1 ? text : text.slice(0, slashIdx);
  const description =
    slashIdx === -1 ? undefined : text.slice(slashIdx + 2).trim() || undefined;

  // Known project names for greedy multi-word matching (e.g. "#Home Improvement").
  const projectByName = new Map(ctx.projects.map((p) => [p.name.toLowerCase(), p.id]));
  const maxProjectWords = ctx.projects.reduce(
    (max, p) => Math.max(max, p.name.trim().split(/\s+/).length),
    1,
  );
  const isToken = (w: string) =>
    PROJECT_RE.test(w) ||
    LABEL_RE.test(w) ||
    PRIORITY_RE.test(w) ||
    ESTIMATE_RE.test(w) ||
    RECURRENCE_RE.test(w);

  // 2. Walk words, pulling out tokens; keep the rest for the title.
  const words = head.split(/\s+/).filter(Boolean);
  const labels: string[] = [];
  let projectId: string | undefined;
  let newProjectName: string | undefined;
  let priority: number | undefined;
  let timeEstimate: string | undefined;
  let recurrenceRule: string | undefined;
  const titleWords: string[] = [];

  for (let i = 0; i < words.length; i++) {
    const word = words[i]!;
    let m: RegExpMatchArray | null;

    if ((m = word.match(PROJECT_RE))) {
      // Greedy: try the longest known project name starting at this "#word",
      // extending only over following plain (non-token) words.
      const first = m[1]!;
      let matched = false;
      const maxExtra = Math.min(maxProjectWords - 1, words.length - 1 - i);
      for (let k = maxExtra; k >= 0; k--) {
        // candidate = "#word"'s name + the next k plain words
        if (k > 0 && words.slice(i + 1, i + 1 + k).some(isToken)) continue;
        const candidate = [first, ...words.slice(i + 1, i + 1 + k)].join(' ');
        const id = projectByName.get(candidate.toLowerCase());
        if (id) {
          projectId = id;
          newProjectName = undefined;
          i += k;
          matched = true;
          break;
        }
      }
      if (!matched) {
        // Unknown → offer to create a project named after the first word only.
        projectId = undefined;
        newProjectName = first;
      }
      continue;
    }
    if ((m = word.match(LABEL_RE))) {
      const label = m[1]!;
      if (!labels.includes(label)) labels.push(label);
      continue;
    }
    if ((m = word.match(PRIORITY_RE))) {
      priority = Number(m[1]);
      continue;
    }
    if ((m = word.match(ESTIMATE_RE))) {
      const bucket = ESTIMATE_BUCKETS[m[1]!.toLowerCase()];
      if (bucket) {
        timeEstimate = bucket;
        continue;
      }
    }
    if ((m = word.match(RECURRENCE_RE))) {
      const rule = m[1]!.toLowerCase();
      if (RECURRENCE_RULES.has(rule)) {
        recurrenceRule = rule;
        continue;
      }
    }

    titleWords.push(word);
  }

  let title = titleWords.join(' ');

  // 3. Extract a date phrase from the leftover title text (chrono).
  let dueDate: string | undefined;
  let dueTime: string | undefined;
  const results = chrono.parse(title, refDate, { forwardDate: true });
  if (results.length > 0) {
    const res = results[0]!;
    const d = res.start.date();
    dueDate = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    if (res.start.isCertain('hour')) {
      dueTime = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
    }
    title = (title.slice(0, res.index) + title.slice(res.index + res.text.length));
  }

  const content = title.replace(/\s+/g, ' ').trim();

  return {
    content,
    ...(description && { description }),
    ...(projectId && { projectId }),
    ...(newProjectName && { newProjectName }),
    labels,
    ...(priority && { priority }),
    ...(timeEstimate && { timeEstimate }),
    ...(recurrenceRule && { recurrenceRule }),
    ...(dueDate && { dueDate }),
    ...(dueTime && { dueTime }),
  };
}
